import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import { supabase } from "../lib/supabase";
import { upsertProfile } from "../lib/profiles";
import { regimeFiscalDepuisOnboarding } from "../lib/dossierFiscalite";
import { createDossier, saveDossierInfos } from "../lib/dossiers";
import { createExercice } from "../lib/exercices";
import { inputFieldClass, selectFieldClass } from "@/lib/ui/formFieldClasses";

type Country = "CI" | "SN" | "CM";
type Step = 1 | 2 | 3 | 4 | 5 | 6;

type RegimeCI = "Entreprenant" | "Micro-Entreprise" | "RSI" | "RRN";
type RegimeSN = "CGU" | "Réel";
type RegimeCM = "Libératoire" | "Simplifié" | "Réel";
type Regime = RegimeCI | RegimeSN | RegimeCM;

const countries: { code: Country; label: string; flag: string; dial: string }[] = [
  { code: "CI", label: "Côte d'Ivoire", flag: "🇨🇮", dial: "+225" },
  { code: "SN", label: "Sénégal", flag: "🇸🇳", dial: "+221" },
  { code: "CM", label: "Cameroun", flag: "🇨🇲", dial: "+237" },
];

const regimesByCountry: Record<Country, Regime[]> = {
  CI: ["Entreprenant", "Micro-Entreprise", "RSI", "RRN"],
  SN: ["CGU", "Réel"],
  CM: ["Libératoire", "Simplifié", "Réel"],
};

function closingForStart(dateIso: string, exerciceLong: boolean): string {
  const [yy, mm] = dateIso.split("-").map(Number);
  const year = yy || new Date().getFullYear();
  const month = mm || 1;
  const closeYear = month > 6 && exerciceLong ? year + 1 : year;
  return `${closeYear}-12-31`;
}

function monthsBetweenInclusive(startIso: string, endIso: string): number {
  const [sy, sm] = startIso.split("-").map(Number);
  const [ey, em] = endIso.split("-").map(Number);
  return (ey - sy) * 12 + (em - sm) + 1;
}

function formatFr(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function passwordScore(pwd: string): { label: string; pct: number; color: string } {
  let s = 0;
  if (pwd.length >= 8) s++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
  if (/\d/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++;
  if (s <= 1) return { label: "Faible", pct: 25, color: "bg-red-400" };
  if (s === 2) return { label: "Moyen", pct: 50, color: "bg-amber-400" };
  if (s === 3) return { label: "Bon", pct: 75, color: "bg-orange-500" };
  return { label: "Fort", pct: 100, color: "bg-emerald-500" };
}

function resolveTypeComptabilite(country: Country, regime: Regime): "tresorerie" | "engagement" {
  if (country === "CI") return regime === "Entreprenant" || regime === "Micro-Entreprise" ? "tresorerie" : "engagement";
  if (country === "SN") return regime === "CGU" ? "tresorerie" : "engagement";
  return regime === "Réel" ? "engagement" : "tresorerie";
}

function regimeLibelleParDefaut(country: Country): Regime {
  if (country === "CI") return "RRN";
  if (country === "SN") return "Réel";
  return "Réel";
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [country, setCountry] = useState<Country | null>(null);
  const dialCode = useMemo(() => countries.find((c) => c.code === country)?.dial ?? "+225", [country]);

  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [telephone, setTelephone] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const pwd = passwordScore(password);

  const [raisonSociale, setRaisonSociale] = useState("");
  const [rccm, setRccm] = useState("");

  const [fiscalId, setFiscalId] = useState("");
  const [regime, setRegime] = useState<Regime | "">("");

  const [dateDebutActivite, setDateDebutActivite] = useState(new Date().toISOString().slice(0, 10));
  const [exerciceLong, setExerciceLong] = useState(false);
  const dateCloture = useMemo(() => closingForStart(dateDebutActivite, exerciceLong), [dateDebutActivite, exerciceLong]);
  const moisExercice = useMemo(() => monthsBetweenInclusive(dateDebutActivite, dateCloture), [dateDebutActivite, dateCloture]);

  const [createdUserId, setCreatedUserId] = useState<string | null>(null);
  const [emailVerificationPending, setEmailVerificationPending] = useState(false);

  const stepTitle: Record<Step, string> = {
    1: "Bienvenue sur Woahda ! 🎉",
    2: "Comment pouvons-nous vous appeler ?",
    3: "Créez votre compte",
    4: "Parlez-nous de votre entreprise",
    5: "Configuration fiscale",
    6: "Configurez votre exercice comptable",
  };
  const stepSubtitle: Record<Step, string> = {
    1: "Dans quel pays est implantée votre entreprise ?",
    2: "Vos informations personnelles",
    3: "Vos accès sécurisés",
    4: "Informations juridiques principales",
    5: country === "CI" ? "NCC et régime d'imposition" : country === "SN" ? "NINEA et régime d'imposition" : "NIU et régime d'imposition",
    6: "Dates de début et clôture du premier exercice",
  };

  const validateStep = (): string | null => {
    if (step === 1 && !country) return "Sélectionnez un pays.";
    if (step === 2) {
      if (!prenom.trim() || !nom.trim()) return "Prénom et nom sont requis.";
      if (!telephone.trim()) return "Téléphone requis.";
    }
    if (step === 3) {
      const okEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
      if (!okEmail) return "Email invalide.";
      if (password.length < 8) return "Le mot de passe doit contenir au moins 8 caractères.";
      if (password !== password2) return "La confirmation du mot de passe ne correspond pas.";
    }
    if (step === 4) {
      if (!raisonSociale.trim()) return "Raison sociale requise.";
      if (!rccm.trim()) return "RCCM / Registre de commerce requis.";
    }
    if (step === 5) {
      if (!fiscalId.trim()) return "Identifiant fiscal requis.";
      if (!regime) return "Sélectionnez un régime d'imposition.";
      if (country === "CI" && !/^\d{7}[A-Za-z]$/.test(fiscalId.trim())) return "NCC invalide (7 chiffres + 1 lettre).";
    }
    if (step === 6) {
      if (!dateDebutActivite?.trim() || !dateCloture?.trim()) return "Dates d'exercice invalides.";
      if (dateDebutActivite > dateCloture) return "La date de début d'activité doit être antérieure ou égale à la date de clôture.";
    }
    return null;
  };

  const goNext = async () => {
    setError(null);

    if (step === 3 && emailVerificationPending) {
      setBusy(true);
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session?.user?.id) {
          setCreatedUserId(data.session.user.id);
          setEmailVerificationPending(false);
          setStep(4);
        } else {
          setError("Aucune session active. Confirmez d'abord l'email reçu, puis réessayez.");
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }

    if (step === 3 && !createdUserId && !emailVerificationPending) {
      setBusy(true);
      try {
        const { data, error: signErr } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: { prenom: prenom.trim(), nom: nom.trim() },
            emailRedirectTo: `${window.location.origin}/onboarding`,
          },
        });
        if (signErr) throw signErr;
        if (data.session?.user?.id) {
          setCreatedUserId(data.session.user.id);
        } else if (data.user?.id) {
          setEmailVerificationPending(true);
          setBusy(false);
          return;
        } else {
          throw new Error("Compte non créé. Vérifiez votre email.");
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur de création de compte");
        setBusy(false);
        return;
      } finally {
        setBusy(false);
      }
    }

    if (step < 6) setStep((s) => (s + 1) as Step);
    else await finishOnboarding();
  };

  const resendOnboardingConfirmation = async () => {
    setBusy(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email.trim(),
        options: { emailRedirectTo: `${window.location.origin}/onboarding` },
      });
      if (error) throw error;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Impossible de renvoyer l'email");
    } finally {
      setBusy(false);
    }
  };

  const finishOnboarding = async () => {
    if (!country) return;
    setBusy(true);
    setError(null);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id ?? createdUserId;
      if (!uid) throw new Error("Session invalide après création du compte.");

      await upsertProfile(uid, {
        prenom: prenom.trim(),
        nom: nom.trim(),
        telephone: `${dialCode} ${telephone.trim()}`.trim(),
        entreprise: raisonSociale.trim(),
        pays: country,
      });

      const regimePourType = regime || regimeLibelleParDefaut(country);
      const typeComptabilite = resolveTypeComptabilite(country, regimePourType);
      const regimeFiscalDossier = regimeFiscalDepuisOnboarding(country, regime || null);
      const dossier = await createDossier({
        nom: raisonSociale.trim(),
        pays: country,
        regime_fiscal: regimeFiscalDossier,
        date_creation: dateDebutActivite,
        date_cloture_exercice: dateCloture,
        exercice_long: exerciceLong,
        type_comptabilite: typeComptabilite,
      });

      await createExercice(dossier.id, {
        date_debut: dateDebutActivite,
        date_fin: dateCloture,
        situation: "nouvelle",
        premier_exercice_woahda: true,
        exercice_long: exerciceLong,
        soldes_importes: false,
        fec_importe: false,
      });

      await saveDossierInfos(dossier.id, {
        denomination: raisonSociale.trim(),
        rccm: rccm.trim(),
        date_debut_activite: dateDebutActivite,
        ncc: country === "CI" ? fiscalId.trim() : null,
        niu: country === "CM" || country === "SN" ? fiscalId.trim() : null,
        regime_imposition: regime || null,
      });

      navigate("/dashboard");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur de finalisation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <style>{`
        @keyframes onb-float-fast {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(8deg); }
        }
        @keyframes onb-float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-15px) rotate(-5deg) scale(1.05); }
        }
        @keyframes onb-pulse-glow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.1); }
        }
        @keyframes onb-spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes onb-blob {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
        }
        @keyframes onb-step-in {
          from { opacity: 0; transform: translateY(8px) translateX(6px); }
          to { opacity: 1; transform: translateY(0) translateX(0); }
        }
        .onb-float-fast { animation: onb-float-fast 4s ease-in-out infinite; }
        .onb-float-slow { animation: onb-float-slow 3.4s ease-in-out infinite; }
        .onb-pulse-glow { animation: onb-pulse-glow 3.2s ease-in-out infinite; }
        .onb-spin-slow { animation: onb-spin-slow 20s linear infinite; }
        .onb-blob { animation: onb-blob 8s ease-in-out infinite; }
        .onb-step-in { animation: onb-step-in 280ms ease-out both; }
      `}</style>

      <div className="w-full bg-gradient-to-br from-slate-300 via-gray-200 to-rose-200/55 lg:w-1/2 flex items-center justify-center p-6 md:p-10 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-6 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500 text-sm font-extrabold text-white">W</div>
            <span className="text-sm font-semibold text-slate-800">Woahda.</span>
          </div>

          <div className="rounded-lg bg-white p-6 shadow-sm md:p-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-500">Etape {step}/6</p>
            <div className="mt-2 h-2 w-full rounded-full bg-slate-200">
              <div className="h-2 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all" style={{ width: `${(step / 6) * 100}%` }} />
            </div>

            <h1 className="mt-4 text-2xl font-bold text-slate-900">{stepTitle[step]}</h1>
            <p className="mt-2 text-sm text-slate-500">{stepSubtitle[step]}</p>

            {error ? <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}

            <div key={step} className="onb-step-in mt-6 space-y-5">
            {step === 1 && (
              <div className="grid gap-3">
                {countries.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => setCountry(c.code)}
                    className={`rounded-lg border-2 p-4 text-left transition-all ${
                      country === c.code ? "border-orange-500 bg-orange-50 shadow-md" : "border-[#e4e8ef] bg-white hover:border-orange-200 hover:bg-orange-50/30"
                    }`}
                  >
                    <div className="text-lg">{c.flag}</div>
                    <div className="mt-1 text-sm font-medium text-slate-900">{c.label}</div>
                  </button>
                ))}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Prénom</label>
                  <input className={inputFieldClass} value={prenom} onChange={(e) => setPrenom(e.target.value)} placeholder="Votre prénom" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Nom</label>
                  <input className={inputFieldClass} value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Votre nom" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Téléphone</label>
                  <div className="flex rounded-lg border border-[#e4e8ef] bg-slate-50 focus-within:border-[#1a2744] focus-within:ring-1 focus-within:ring-[#1a2744]/20">
                    <span className="flex items-center border-r border-[#e4e8ef] px-3 text-sm text-slate-600">{dialCode}</span>
                    <input className="w-full bg-transparent px-4 py-3 text-sm focus:outline-none" value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="Numéro de téléphone" />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
                  <input type="email" className={inputFieldClass} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@entreprise.com" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Mot de passe</label>
                  <input type="password" className={inputFieldClass} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 8 caractères" />
                  <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200">
                    <div className={`h-1.5 rounded-full ${pwd.color}`} style={{ width: `${pwd.pct}%` }} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Force : {pwd.label}</p>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Confirmer mot de passe</label>
                  <input type="password" className={inputFieldClass} value={password2} onChange={(e) => setPassword2(e.target.value)} placeholder="Retapez votre mot de passe" />
                </div>
                {emailVerificationPending && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                    <p className="m-0 font-medium">Vérifiez votre boîte mail</p>
                    <p className="mt-2 text-amber-900/90">
                      Un lien de confirmation a été envoyé à <strong>{email.trim()}</strong>. Après clic, revenez ici et appuyez sur le bouton ci-dessous.
                    </p>
                    <button
                      type="button"
                      onClick={resendOnboardingConfirmation}
                      disabled={busy}
                      className="mt-3 text-sm font-semibold text-orange-600 underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      Renvoyer l&apos;email
                    </button>
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Raison sociale</label>
                  <input className={inputFieldClass} value={raisonSociale} onChange={(e) => setRaisonSociale(e.target.value)} placeholder="Ex: Alpha SARL" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">RCCM / Registre de commerce</label>
                  <input className={inputFieldClass} value={rccm} onChange={(e) => setRccm(e.target.value)} placeholder="Ex: CI-ABJ-2026-B-12345" />
                </div>
              </div>
            )}

            {step === 5 && country && (
              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {country === "CI" ? "NCC" : country === "SN" ? "NINEA" : "NIU"}
                  </label>
                  <input className={inputFieldClass} value={fiscalId} onChange={(e) => setFiscalId(e.target.value)} placeholder={country === "CI" ? "1234567X" : country === "SN" ? "NINEA..." : "NIU..."} />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Régime d'imposition</label>
                  <select className={selectFieldClass} value={regime} onChange={(e) => setRegime(e.target.value as Regime)}>
                    <option value="">Choisir un régime</option>
                    {regimesByCountry[country].map((r) => (
                      <option key={r} value={r}>
                        {r === "RSI" ? "Réel Simplifié (RSI)" : r === "RRN" ? "Réel Normal (RRN)" : r === "CGU" ? "Contribution Globale Unique (CGU)" : r === "Réel" ? "Régime du Réel" : r === "Libératoire" ? "Régime Libératoire" : r === "Simplifié" ? "Régime Simplifié" : r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {step === 6 && (
              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Date de début d'activité</label>
                  <input type="date" className={inputFieldClass} value={dateDebutActivite} onChange={(e) => setDateDebutActivite(e.target.value)} />
                </div>

                {Number(dateDebutActivite.split("-")[1] || 1) > 6 ? (
                  <label className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50/50 p-3 text-sm text-slate-700">
                    <input type="checkbox" className="mt-0.5 accent-orange-500" checked={exerciceLong} onChange={(e) => setExerciceLong(e.target.checked)} />
                    <span>Opter pour un exercice long (clôture au 31/12/N+1)</span>
                  </label>
                ) : (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    Votre exercice se clôturera le 31/12/{dateDebutActivite.slice(0, 4)}.
                  </div>
                )}

                <div className="rounded-lg border border-[#e4e8ef] bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <p>
                    Premier exercice : du <span className="font-semibold">{formatFr(dateDebutActivite)}</span> au{" "}
                    <span className="font-semibold">{formatFr(dateCloture)}</span> ({moisExercice} mois)
                  </p>
                </div>
              </div>
            )}
            </div>

            <div className="mt-8 space-y-3">
              <Button
                type="button"
                variant="primary"
                size="md"
                loading={busy}
                onClick={() => void goNext()}
                className="w-full font-semibold"
              >
                {step === 6
                  ? "Terminer"
                  : step === 3 && emailVerificationPending
                    ? "J'ai confirmé mon email — continuer"
                    : "Suivant"}
              </Button>
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => setStep((s) => (s > 1 ? ((s - 1) as Step) : s))} className="text-sm text-slate-500 transition-colors hover:text-slate-700">
                  Retour
                </button>
                <p className="text-sm text-slate-500">
                  Déjà un compte ?{" "}
                  <Link to="/auth" className="font-medium text-orange-500">
                    Se connecter
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative hidden w-1/2 overflow-hidden lg:flex items-center justify-center bg-[linear-gradient(135deg,#ea580c_0%,#f97316_30%,#fb923c_50%,#fbbf24_70%,#f472b6_100%)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_18%,rgba(255,255,255,0.22),transparent_38%),radial-gradient(circle_at_84%_76%,rgba(255,107,107,0.18),transparent_42%),radial-gradient(circle_at_52%_42%,rgba(251,191,36,0.14),transparent_40%)]" />

        <div className="onb-pulse-glow absolute -left-20 -top-20 h-96 w-96 rounded-full bg-amber-300/40 blur-3xl" />
        <div className="onb-float-fast absolute right-20 top-32 h-48 w-48 rounded-full bg-gradient-to-br from-rose-400/30 to-orange-400/30" />
        <div className="onb-spin-slow absolute bottom-40 left-20 h-32 w-32 rounded-lg border-2 border-white/20" />
        <div className="onb-float-slow absolute left-1/4 top-1/2 h-24 w-24 rounded-full bg-amber-400/40" style={{ animationDelay: "1s" }} />
        <div className="onb-blob absolute bottom-20 right-32 h-40 w-40 bg-gradient-to-tr from-orange-300/40 to-rose-300/30" style={{ animationDuration: "5s" }} />

        <div className="absolute top-20 right-1/3 h-4 w-4 animate-pulse rounded-full bg-white/40" />
        <div className="absolute bottom-32 right-1/4 h-3 w-3 animate-pulse rounded-full bg-white/30" style={{ animationDelay: "0.5s" }} />
        <div className="absolute left-1/2 top-1/3 h-2 w-2 animate-pulse rounded-full bg-white/50" style={{ animationDelay: "1s" }} />
        <div className="absolute left-10 top-1/4 h-2.5 w-2.5 animate-pulse rounded-full bg-white/40" style={{ animationDelay: "1.4s" }} />

        <div className="absolute left-1/2 top-0 h-32 w-px bg-gradient-to-b from-white/0 via-white/20 to-white/0" />
        <div className="absolute bottom-0 right-1/3 h-24 w-px bg-gradient-to-t from-white/0 via-white/20 to-white/0" />
        <div className="absolute left-0 top-0 h-full w-full bg-[linear-gradient(120deg,transparent_35%,rgba(255,255,255,0.10)_35%,rgba(255,255,255,0.10)_36%,transparent_36%,transparent_64%,rgba(255,255,255,0.10)_64%,rgba(255,255,255,0.10)_65%,transparent_65%)] opacity-40" />

        <svg className="absolute bottom-0 left-0 w-full opacity-30" viewBox="0 0 800 140" fill="none" preserveAspectRatio="none">
          <path d="M0 70C120 120 230 10 360 45C470 75 560 135 800 65V140H0V70Z" fill="white" />
        </svg>

        <div className="relative z-10 px-12 text-center text-white">
          <h2 className="text-4xl font-bold leading-tight drop-shadow-sm">
            Gérez votre comptabilité
            <br />
            en toute sérénité
          </h2>
          <div className="mt-5 flex items-center justify-center gap-3 text-sm font-medium text-white/90">
            <span className="flex items-center gap-1">🇨🇮 Côte d&apos;Ivoire</span>
            <span className="h-1 w-1 rounded-full bg-white/50" />
            <span className="flex items-center gap-1">🇸🇳 Sénégal</span>
            <span className="h-1 w-1 rounded-full bg-white/50" />
            <span className="flex items-center gap-1">🇨🇲 Cameroun</span>
          </div>
          <p className="mt-8 text-lg font-light italic text-white/70">
            "La comptabilité OHADA, simplifiée."
          </p>
        </div>
      </div>
    </div>
  );
}
